import React, { useState, useEffect } from 'react';
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Slider,
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Tabs,
  Tab,
  Box,
  Divider,
  Alert
} from '@mui/material';
import { ContigSelector } from './ContigSelector';
import BinderDesignForm from './RFDiffusionModes/BinderDesignForm';

interface FormValues {
  mode?: string;
  input_pdb_file?: File;
  input_pdb?: string;
  sequence?: string;
  contigs?: string;
  num_designs?: number;
  chain_length?: number;
  symmetry?: string;
  use_multimer?: boolean;
  [key: string]: unknown;
}

interface ErrorState {
  input_pdb?: string;
  input_pdb_file?: string;
  contigs?: string;
  hotspot_residues?: string;
  [key: string]: string | undefined;
}

interface RFDiffusionFormProps {
  params: FormValues;
  onChange: (values: FormValues) => void;
}

export default function RFDiffusionForm({
  params,
  onChange
}: RFDiffusionFormProps) {
  const [mode, setMode] = useState<string>(params.mode || 'hallucination');
  const [pdbContent, setPdbContent] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorState>({});
  
  // Load PDB content if a file is selected or if input_pdb is provided
  useEffect(() => {
    if (params.input_pdb_file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setPdbContent(content);
      };
      reader.readAsText(params.input_pdb_file);
    } else if (params.input_pdb) {
      // Use the PDB content directly if provided
      setPdbContent(params.input_pdb);
    } else {
      setPdbContent(null);
    }
  }, [params.input_pdb_file, params.input_pdb]);

  const handleChange = (field: string, value: unknown) => {
    onChange({ ...params, [field]: value });
    
    // Clear errors for the field
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  // Handle contig change from the ContigSelector
  const handleContigChange = (value: string) => {
    handleChange('contigs', value);
  };

  const handleModeChange = (_event: React.SyntheticEvent, newValue: string) => {
    setMode(newValue);
    handleChange('mode', newValue);
  };

  const requiresPDB = mode === 'scaffold' || mode === 'binder' || (params.contigs && params.contigs.trim() !== '');

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Tabs
            value={mode}
            onChange={handleModeChange}
            aria-label="RFDiffusion mode tabs"
            variant="fullWidth"
          >
            <Tab label="Free Hallucination" value="hallucination" />
            <Tab label="Scaffold-Guided" value="scaffold" />
            <Tab label="Binder Design" value="binder" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {mode === 'hallucination' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Generate novel protein structures without constraints
              </Alert>
            )}
            {mode === 'scaffold' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Design structures based on an input protein scaffold
              </Alert>
            )}
            {mode === 'binder' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Design a protein that binds to a specific region of your input protein
              </Alert>
            )}
          </Box>

          {requiresPDB && !params.input_pdb_file && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This mode requires a PDB file input. Please upload a PDB file first.
            </Alert>
          )}

          <Grid container spacing={3}>
            {mode !== 'binder' && (
              <Grid item xs={12}>
                {requiresPDB && !pdbContent ? (
                  <Alert severity="warning">
                    You need to upload a PDB file to select contigs
                  </Alert>
                ) : (
                  <Box>
                    <ContigSelector
                      pdbContent={pdbContent}
                      onChange={handleContigChange}
                      value={params.contigs || ''}
                    />
                    {errors.contigs && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {errors.contigs}
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {mode === 'binder' ? (
            <BinderDesignForm
              formValues={params}
              handleChange={handleChange}
              errors={errors}
              pdbContent={pdbContent}
            />
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="num-designs-label">Number of Designs</InputLabel>
                  <Select
                    labelId="num-designs-label"
                    value={params.num_designs || 1}
                    label="Number of Designs"
                    onChange={(e) => handleChange('num_designs', Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography gutterBottom>
                  Chain Length: {params.chain_length || 100}
                </Typography>
                <Slider
                  value={params.chain_length || 100}
                  onChange={(_, value) => handleChange('chain_length', value as number)}
                  step={10}
                  min={50}
                  max={300}
                  valueLabelDisplay="auto"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="symmetry-label">Symmetry</InputLabel>
                  <Select
                    labelId="symmetry-label"
                    value={params.symmetry || 'none'}
                    label="Symmetry"
                    onChange={(e) => handleChange('symmetry', e.target.value)}
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="C2">C2</MenuItem>
                    <MenuItem value="C3">C3</MenuItem>
                    <MenuItem value="C4">C4</MenuItem>
                    <MenuItem value="D2">D2</MenuItem>
                    <MenuItem value="D3">D3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={params.use_multimer || false}
                      onChange={(e) => handleChange('use_multimer', e.target.checked)}
                    />
                  }
                  label="Use AlphaFold Multimer"
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
} 